'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  X, Calendar, Users, Settings, BarChart3, GitBranch, AlertCircle,
  ChevronRight, ChevronLeft, Check, Info,
} from 'lucide-react';
import type { AppraisalCycle } from '@/store/appraisalStore';
import { MOCK_TEMPLATES } from '@/data/mockData';
import Icon from '@/components/ui/AppIcon';


interface Props {
  cycle: AppraisalCycle | null;
  onClose: () => void;
  onSave: (cycle: AppraisalCycle) => void;
}

type FormData = Omit<AppraisalCycle, 'id' | 'submitted' | 'managerReviewed' | 'hrReviewed' | 'completed' | 'totalAssigned' | 'createdAt' | 'updatedAt' | 'createdBy'>;

const STEPS = [
  { id: 'step-basic', label: 'Basic Info', icon: Calendar },
  { id: 'step-windows', label: 'Review Windows', icon: Users },
  { id: 'step-config', label: 'Configuration', icon: Settings },
  { id: 'step-scoring', label: 'Scoring', icon: BarChart3 },
];

const SCORING_LABELS: Record<number, string> = { 3: 'Basic (1–3)', 4: 'Standard (1–4)', 5: 'Detailed (1–5)', 7: 'Extended (1–7)', 10: 'Granular (1–10)' };

export default function CycleFormModal({ cycle, onClose, onSave }: Props) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scoringScale, setScoringScale] = useState(cycle?.scoringScale || 5);
  const [approvalLevels, setApprovalLevels] = useState(cycle?.approvalLevels || 2);
  const [autoClose, setAutoClose] = useState(cycle?.autoCloseEnabled ?? true);
  const [freezeOnSubmit, setFreezeOnSubmit] = useState(cycle?.freezeOnSubmit ?? false);
  const [sequential, setSequential] = useState(cycle?.sequentialApproval ?? true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
  } = useForm<FormData>({
    defaultValues: cycle
      ? {
          name: cycle.name,
          description: cycle.description,
          year: cycle.year,
          status: cycle.status,
          startDate: cycle.startDate,
          endDate: cycle.endDate,
          employeeWindowStart: cycle.employeeWindowStart,
          employeeWindowEnd: cycle.employeeWindowEnd,
          managerWindowStart: cycle.managerWindowStart,
          managerWindowEnd: cycle.managerWindowEnd,
          hrWindowStart: cycle.hrWindowStart,
          hrWindowEnd: cycle.hrWindowEnd,
          department: cycle.department,
          templateId: cycle.templateId,
          templateName: cycle.templateName,
        }
      : {
          year: 2025,
          status: 'draft',
          department: 'All Departments',
        },
  });

  const STEP_FIELDS: Record<number, (keyof FormData)[]> = {
    0: ['name', 'year', 'department', 'templateId', 'startDate', 'endDate'],
    1: ['employeeWindowStart', 'employeeWindowEnd', 'managerWindowStart', 'managerWindowEnd', 'hrWindowStart', 'hrWindowEnd'],
    2: [],
    3: [],
  };

  const handleNext = async () => {
    const valid = await trigger(STEP_FIELDS[step] as any);
    if (valid) setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 600));
    const saved: AppraisalCycle = {
      id: cycle?.id || `cycle-${Date.now()}`,
      ...data,
      scoringScale,
      approvalLevels,
      autoCloseEnabled: autoClose,
      freezeOnSubmit,
      sequentialApproval: sequential,
      submitted: cycle?.submitted || 0,
      managerReviewed: cycle?.managerReviewed || 0,
      hrReviewed: cycle?.hrReviewed || 0,
      completed: cycle?.completed || 0,
      totalAssigned: cycle?.totalAssigned || 0,
      createdBy: cycle?.createdBy || 'Priya Nair',
      createdAt: cycle?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      templateName: MOCK_TEMPLATES.find((t) => t.id === data.templateId)?.name || data.templateName || '',
    };
    setLoading(false);
    onSave(saved);
  };

  return (
    <div className="pn-appraisal__modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cycle-modal-title">
      <div className="pn-appraisal__cycle-modal">
        {/* Header */}
        <div className="pn-appraisal__cycle-modal-header">
          <div className="pn-appraisal__cycle-modal-header-info">
            <h2 className="pn-appraisal__cycle-modal-title" id="cycle-modal-title">
              {cycle ? 'Edit Appraisal Cycle' : 'Create New Appraisal Cycle'}
            </h2>
            <p className="pn-appraisal__cycle-modal-subtitle">
              {cycle ? `Editing "${cycle.name}"` : 'Configure a new performance review cycle'}
            </p>
          </div>
          <button className="pn-appraisal__cycle-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="pn-appraisal__cycle-modal-steps">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const isDone = idx < step;
            const isActive = idx === step;
            return (
              <React.Fragment key={s.id}>
                <div className={`pn-appraisal__cycle-step${isDone ? ' pn-appraisal__cycle-step--done' : isActive ? ' pn-appraisal__cycle-step--active' : ' pn-appraisal__cycle-step--pending'}`}>
                  <div className="pn-appraisal__cycle-step-circle">
                    {isDone ? <Check size={14} /> : <Icon size={14} />}
                  </div>
                  <span className="pn-appraisal__cycle-step-label">{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`pn-appraisal__cycle-step-line${isDone ? ' pn-appraisal__cycle-step-line--done' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="pn-appraisal__cycle-modal-body">

            {/* ── Step 0: Basic Info ── */}
            {step === 0 && (
              <div className="pn-appraisal__cycle-modal-step-content">
                <div className="pn-appraisal__cycle-modal-section">
                  <div className="pn-appraisal__cycle-modal-section-title">
                    <Calendar size={15} /> Cycle Details
                  </div>

                  <div className="pn-appraisal__form-field">
                    <label className="pn-appraisal__form-label" htmlFor="cycle-name">
                      Cycle Name <span className="pn-appraisal__form-label-required">*</span>
                    </label>
                    <p className="pn-appraisal__form-hint">A clear, descriptive name visible to employees and managers</p>
                    <input
                      id="cycle-name"
                      className={`pn-appraisal__form-input${errors.name ? ' pn-appraisal__form-input--error' : ''}`}
                      placeholder="e.g. Annual Performance Review 2025"
                      {...register('name', { required: 'Cycle name is required', minLength: { value: 5, message: 'Name must be at least 5 characters' } })}
                    />
                    {errors.name && (
                      <p className="pn-appraisal__form-error"><AlertCircle size={12} /> {errors.name.message}</p>
                    )}
                  </div>

                  <div className="pn-appraisal__cycle-modal-row">
                    <div className="pn-appraisal__form-field">
                      <label className="pn-appraisal__form-label" htmlFor="cycle-year">
                        Appraisal Year <span className="pn-appraisal__form-label-required">*</span>
                      </label>
                      <select
                        id="cycle-year"
                        className={`pn-appraisal__form-select${errors.year ? ' pn-appraisal__form-input--error' : ''}`}
                        {...register('year', { required: 'Year is required', valueAsNumber: true })}
                      >
                        {[2024, 2025, 2026, 2027].map((y) => (
                          <option key={`yr-${y}`} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>

                    <div className="pn-appraisal__form-field">
                      <label className="pn-appraisal__form-label" htmlFor="cycle-status">
                        Initial Status
                      </label>
                      <select id="cycle-status" className="pn-appraisal__form-select" {...register('status')}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>

                  <div className="pn-appraisal__form-field">
                    <label className="pn-appraisal__form-label" htmlFor="cycle-dept">
                      Department Scope <span className="pn-appraisal__form-label-required">*</span>
                    </label>
                    <select
                      id="cycle-dept"
                      className="pn-appraisal__form-select"
                      {...register('department', { required: 'Department is required' })}
                    >
                      <option value="All Departments">All Departments</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Engineering, Product">Engineering, Product</option>
                      <option value="Sales">Sales</option>
                      <option value="Sales, Operations">Sales, Operations</option>
                      <option value="Customer Support">Customer Support</option>
                      <option value="Finance">Finance</option>
                      <option value="HR">HR</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Operations">Operations</option>
                    </select>
                  </div>

                  <div className="pn-appraisal__form-field">
                    <label className="pn-appraisal__form-label" htmlFor="cycle-template">
                      Appraisal Template <span className="pn-appraisal__form-label-required">*</span>
                    </label>
                    <p className="pn-appraisal__form-hint">Select the question template to use for this cycle</p>
                    <select
                      id="cycle-template"
                      className={`pn-appraisal__form-select${errors.templateId ? ' pn-appraisal__form-input--error' : ''}`}
                      {...register('templateId', { required: 'Template is required' })}
                    >
                      <option value="">Select a template…</option>
                      {MOCK_TEMPLATES.filter((t) => t.status === 'active').map((t) => (
                        <option key={`tmpl-opt-${t.id}`} value={t.id}>{t.name} ({t.version})</option>
                      ))}
                    </select>
                    {errors.templateId && <p className="pn-appraisal__form-error"><AlertCircle size={12} /> {errors.templateId.message}</p>}
                  </div>
                </div>

                <div className="pn-appraisal__cycle-modal-section">
                  <div className="pn-appraisal__cycle-modal-section-title">
                    <Calendar size={15} /> Cycle Duration
                  </div>
                  <div className="pn-appraisal__cycle-modal-row">
                    <div className="pn-appraisal__form-field">
                      <label className="pn-appraisal__form-label" htmlFor="cycle-start">
                        Start Date <span className="pn-appraisal__form-label-required">*</span>
                      </label>
                      <input
                        id="cycle-start"
                        type="date"
                        className={`pn-appraisal__form-input${errors.startDate ? ' pn-appraisal__form-input--error' : ''}`}
                        {...register('startDate', { required: 'Start date is required' })}
                      />
                      {errors.startDate && <p className="pn-appraisal__form-error"><AlertCircle size={12} /> {errors.startDate.message}</p>}
                    </div>
                    <div className="pn-appraisal__form-field">
                      <label className="pn-appraisal__form-label" htmlFor="cycle-end">
                        End Date <span className="pn-appraisal__form-label-required">*</span>
                      </label>
                      <input
                        id="cycle-end"
                        type="date"
                        className={`pn-appraisal__form-input${errors.endDate ? ' pn-appraisal__form-input--error' : ''}`}
                        {...register('endDate', { required: 'End date is required' })}
                      />
                      {errors.endDate && <p className="pn-appraisal__form-error"><AlertCircle size={12} /> {errors.endDate.message}</p>}
                    </div>
                  </div>

                  <div className="pn-appraisal__form-field">
                    <label className="pn-appraisal__form-label" htmlFor="cycle-desc">
                      Description <span className="pn-appraisal__form-label-hint">(optional)</span>
                    </label>
                    <textarea
                      id="cycle-desc"
                      className="pn-appraisal__form-textarea"
                      placeholder="Brief description of this appraisal cycle's purpose and scope…"
                      rows={3}
                      {...register('description')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 1: Review Windows ── */}
            {step === 1 && (
              <div className="pn-appraisal__cycle-modal-step-content">
                <div className="pn-appraisal__cycle-modal-info-banner">
                  <Info size={14} />
                  <span>Review windows define when each role can access and submit their appraisal forms. Windows should be sequential.</span>
                </div>

                {[
                  { label: 'Employee Review Window', icon: Users, startKey: 'employeeWindowStart' as keyof FormData, endKey: 'employeeWindowEnd' as keyof FormData, startId: 'emp-start', endId: 'emp-end', color: 'var(--pn-primary)' },
                  { label: 'Manager Review Window', icon: Users, startKey: 'managerWindowStart' as keyof FormData, endKey: 'managerWindowEnd' as keyof FormData, startId: 'mgr-start', endId: 'mgr-end', color: 'var(--pn-accent)' },
                  { label: 'HR Final Review Window', icon: Users, startKey: 'hrWindowStart' as keyof FormData, endKey: 'hrWindowEnd' as keyof FormData, startId: 'hr-start', endId: 'hr-end', color: 'var(--pn-success)' },
                ].map((win) => {
                  const WinIcon = win.icon;
                  const startErr = errors[win.startKey];
                  const endErr = errors[win.endKey];
                  return (
                    <div key={win.startId} className="pn-appraisal__cycle-modal-section">
                      <div className="pn-appraisal__cycle-modal-section-title" style={{ color: win.color }}>
                        <WinIcon size={15} /> {win.label}
                      </div>
                      <div className="pn-appraisal__cycle-modal-row">
                        <div className="pn-appraisal__form-field">
                          <label className="pn-appraisal__form-label" htmlFor={win.startId}>
                            Window Opens <span className="pn-appraisal__form-label-required">*</span>
                          </label>
                          <input
                            id={win.startId}
                            type="date"
                            className={`pn-appraisal__form-input${startErr ? ' pn-appraisal__form-input--error' : ''}`}
                            {...register(win.startKey, { required: 'Required' })}
                          />
                          {startErr && <p className="pn-appraisal__form-error"><AlertCircle size={12} /> {startErr.message as string}</p>}
                        </div>
                        <div className="pn-appraisal__form-field">
                          <label className="pn-appraisal__form-label" htmlFor={win.endId}>
                            Window Closes <span className="pn-appraisal__form-label-required">*</span>
                          </label>
                          <input
                            id={win.endId}
                            type="date"
                            className={`pn-appraisal__form-input${endErr ? ' pn-appraisal__form-input--error' : ''}`}
                            {...register(win.endKey, { required: 'Required' })}
                          />
                          {endErr && <p className="pn-appraisal__form-error"><AlertCircle size={12} /> {endErr.message as string}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Step 2: Configuration ── */}
            {step === 2 && (
              <div className="pn-appraisal__cycle-modal-step-content">
                <div className="pn-appraisal__cycle-modal-section">
                  <div className="pn-appraisal__cycle-modal-section-title">
                    <Settings size={15} /> Submission Rules
                  </div>
                  <div className="pn-appraisal__cycle-modal-toggles">
                    {[
                      {
                        key: 'autoClose',
                        label: 'Auto-close cycle on end date',
                        desc: 'Automatically transition cycle to Closed status when end date is reached',
                        value: autoClose,
                        setter: setAutoClose,
                      },
                      {
                        key: 'freeze',
                        label: 'Freeze form after employee submission',
                        desc: 'Employee cannot edit their appraisal form once submitted',
                        value: freezeOnSubmit,
                        setter: setFreezeOnSubmit,
                      },
                      {
                        key: 'sequential',
                        label: 'Sequential approval flow',
                        desc: 'Manager review must complete before HR review window opens',
                        value: sequential,
                        setter: setSequential,
                      },
                    ].map((toggle) => (
                      <div key={toggle.key} className="pn-appraisal__form-toggle">
                        <div className="pn-appraisal__form-toggle-info">
                          <div className="pn-appraisal__form-toggle-label">{toggle.label}</div>
                          <div className="pn-appraisal__form-toggle-desc">{toggle.desc}</div>
                        </div>
                        <button
                          type="button"
                          className={`pn-appraisal__form-toggle-switch${toggle.value ? ' pn-appraisal__form-toggle-switch--on' : ''}`}
                          onClick={() => toggle.setter(!toggle.value)}
                          aria-label={`Toggle ${toggle.label}`}
                          role="switch"
                          aria-checked={toggle.value}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pn-appraisal__cycle-modal-section">
                  <div className="pn-appraisal__cycle-modal-section-title">
                    <GitBranch size={15} /> Approval Hierarchy
                  </div>
                  <div className="pn-appraisal__form-field">
                    <label className="pn-appraisal__form-label">Maximum Approval Levels</label>
                    <p className="pn-appraisal__form-hint">Number of hierarchical manager levels that must approve before HR review</p>
                    <div className="pn-appraisal__cycle-modal-level-grid">
                      {[1, 2, 3, 4].map((n) => (
                        <button
                          key={`level-${n}`}
                          type="button"
                          onClick={() => setApprovalLevels(n)}
                          className={`pn-appraisal__cycle-modal-level-btn${approvalLevels === n ? ' pn-appraisal__cycle-modal-level-btn--active' : ''}`}
                        >
                          <span className="pn-appraisal__cycle-modal-level-num">{n}</span>
                          <span className="pn-appraisal__cycle-modal-level-desc">
                            {n === 1 ? 'Direct manager' : n === 2 ? 'Direct + skip-level' : n === 3 ? 'Full chain' : 'VP/C-level'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 3: Scoring ── */}
            {step === 3 && (
              <div className="pn-appraisal__cycle-modal-step-content">
                <div className="pn-appraisal__cycle-modal-section">
                  <div className="pn-appraisal__cycle-modal-section-title">
                    <BarChart3 size={15} /> Rating Scale
                  </div>
                  <div className="pn-appraisal__form-field">
                    <label className="pn-appraisal__form-label">Select Scoring Scale</label>
                    <p className="pn-appraisal__form-hint">Choose the maximum rating value for this cycle</p>
                    <div className="pn-appraisal__cycle-modal-scale-grid">
                      {[3, 4, 5, 7, 10].map((n) => (
                        <button
                          key={`scale-${n}`}
                          type="button"
                          onClick={() => setScoringScale(n)}
                          className={`pn-appraisal__cycle-modal-scale-btn${scoringScale === n ? ' pn-appraisal__cycle-modal-scale-btn--active' : ''}`}
                        >
                          <span className="pn-appraisal__cycle-modal-scale-num">{n}</span>
                          <span className="pn-appraisal__cycle-modal-scale-label">{SCORING_LABELS[n]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pn-appraisal__cycle-modal-section">
                  <div className="pn-appraisal__cycle-modal-section-title">
                    <BarChart3 size={15} /> Rating Labels
                  </div>
                  <div className="pn-appraisal__cycle-modal-rating-list">
                    {Array.from({ length: scoringScale }, (_, i) => {
                      const rating = scoringScale - i;
                      const defaultLabels: Record<number, Record<number, string>> = {
                        5: { 5: 'Outstanding', 4: 'Exceeds Expectations', 3: 'Meets Expectations', 2: 'Needs Improvement', 1: 'Unsatisfactory' },
                        4: { 4: 'Exceptional', 3: 'Meets & Exceeds', 2: 'Meets Expectations', 1: 'Below Expectations' },
                        3: { 3: 'Exceeds', 2: 'Meets', 1: 'Below' },
                        7: { 7: 'Outstanding', 6: 'Excellent', 5: 'Very Good', 4: 'Good', 3: 'Satisfactory', 2: 'Needs Improvement', 1: 'Unsatisfactory' },
                        10: { 10: 'Elite', 9: 'Outstanding', 8: 'Excellent', 7: 'Very Good', 6: 'Good', 5: 'Satisfactory', 4: 'Fair', 3: 'Needs Work', 2: 'Poor', 1: 'Unacceptable' },
                      };
                      const label = defaultLabels[scoringScale]?.[rating] || `Rating ${rating}`;
                      const pct = rating / scoringScale;
                      const color = pct >= 0.8 ? 'var(--pn-success)' : pct >= 0.6 ? 'var(--pn-primary)' : pct >= 0.4 ? 'var(--pn-warning)' : 'var(--pn-danger)';
                      const bg = pct >= 0.8 ? 'var(--pn-success-light)' : pct >= 0.6 ? 'var(--pn-primary-light)' : pct >= 0.4 ? 'var(--pn-warning-light)' : 'var(--pn-danger-light)';
                      return (
                        <div key={`rating-row-${rating}`} className="pn-appraisal__cycle-modal-rating-row">
                          <div className="pn-appraisal__cycle-modal-rating-badge" style={{ background: bg, color }}>
                            {rating}
                          </div>
                          <input
                            className="pn-appraisal__form-input"
                            defaultValue={label}
                            aria-label={`Label for rating ${rating}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Summary */}
                <div className="pn-appraisal__cycle-modal-summary">
                  <div className="pn-appraisal__cycle-modal-summary-title">Configuration Summary</div>
                  <div className="pn-appraisal__cycle-modal-summary-grid">
                    {[
                      ['Rating Scale', `1 – ${scoringScale}`],
                      ['Approval Levels', String(approvalLevels)],
                      ['Sequential Approval', sequential ? 'Yes' : 'No'],
                      ['Auto-close', autoClose ? 'Enabled' : 'Disabled'],
                      ['Freeze on Submit', freezeOnSubmit ? 'Yes' : 'No'],
                    ].map(([label, value]) => (
                      <div key={`sum-${label}`} className="pn-appraisal__cycle-modal-summary-item">
                        <span className="pn-appraisal__cycle-modal-summary-key">{label}</span>
                        <span className="pn-appraisal__cycle-modal-summary-val">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="pn-appraisal__cycle-modal-footer">
            <div className="pn-appraisal__cycle-modal-footer-step">
              Step {step + 1} of {STEPS.length}
            </div>
            <div className="pn-appraisal__cycle-modal-footer-actions">
              <button
                type="button"
                className="pn-appraisal__btn pn-appraisal__btn--secondary"
                onClick={() => step === 0 ? onClose() : setStep((s) => s - 1)}
              >
                {step === 0 ? 'Cancel' : <><ChevronLeft size={14} /> Back</>}
              </button>

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  className="pn-appraisal__btn pn-appraisal__btn--primary"
                  onClick={handleNext}
                >
                  Next <ChevronRight size={14} />
                </button>
              ) : (
                <button
                  type="submit"
                  className={`pn-appraisal__btn pn-appraisal__btn--primary${loading ? ' pn-appraisal__btn--loading' : ''}`}
                  disabled={loading}
                >
                  {!loading && <Check size={14} />}
                  {loading ? 'Saving…' : cycle ? 'Save Changes' : 'Create Cycle'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}